/**
 * SnowflakeSetupStep — phase 3b: the customer runs RudderStack's setup SQL in Snowflake, then clicks
 * Verify.
 *
 * The key pair is RudderStack's (config-backend generated it; Lookout never sees the private key), so
 * this step never mounts `SnowflakeForm` or `useSnowflakeKeypair` and never touches the workspace key
 * pair. A failed verify keeps the setup server-side: fix the SQL and click Verify again. An expired or
 * vanished setup offers Start again.
 *
 * ACT2-1031: the optional Lookout compute warehouse is collected here and sent with Verify. Lookout
 * stores it on the connection; RudderStack never receives it, so reverse ETL keeps the warehouse the
 * setup was started with.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { CopyButton } from '@/_shared/components/CopyButton';
import { Button, ButtonGroup } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { Form, FormField } from '@/_shared/components/ui/form';
import { Input } from '@/_shared/components/ui/input';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { useSyntaxStyle } from '@/_shared/hooks/useSyntaxStyle';
import { codeBlockCustomStyle, SyntaxHighlighter } from '@/_shared/lib/syntax';

import { lookoutWarehouseGrantSQL } from '../../../lib/snowflake-setup';
import { refreshPermissionOnRefusal } from '../accountCreatePermission';
import { refusalMessage } from '../refusalCopy';
import { DownloadButton } from '../shared/DownloadButton';
import { FailedStepCallout } from '../shared/FailedStepCallout';
import { FormSection } from '../shared/FormSection';
import { OutputSection } from '../shared/OutputSection';
import { LOOKOUT_WAREHOUSE_HINT, optionalWarehouseNameSchema } from '../snowflake/lookoutWarehouseField';
import type { FailedStep } from './PlatformAccountStep';

/** View model of a pending setup: only what this step renders. */
export interface SetupView {
  id: string;
  name?: string;
  publicKey: { pem: string; fingerprint: string };
  setupSql: string;
  expiresAt: string;
  /** The setup's non-secret account options; `role` names the role the extra warehouse grant goes to. */
  options?: Record<string, unknown>;
}

const LookoutWarehouseSchema = z.object({ lookoutWarehouse: optionalWarehouseNameSchema });
type LookoutWarehouseValues = z.infer<typeof LookoutWarehouseSchema>;

export interface SetupVerifyHandle {
  verify: () => void;
}

interface SnowflakeSetupStepProps {
  setup: SetupView;
  onVerified: () => void;
  onRestart: () => void;
  onBusyChange: (busy: boolean) => void;
  /** Phase 3c: Reconnect — the verify re-binds this paused connection in place. */
  reconnectConnectionId?: string;
  /**
   * ACT2-1031: the connection's stored Lookout compute warehouse on a reconnect. Prefilled so its grant
   * is shown for the new role; the field is always sent, so clearing it clears the override.
   */
  initialLookoutWarehouse?: string | null;
}

interface RpcErrorShape {
  data?: { failedStep?: FailedStep; reason?: string };
}

export const SnowflakeSetupStep = React.forwardRef<SetupVerifyHandle, SnowflakeSetupStepProps>(
  function SnowflakeSetupStep(
    { setup, onVerified, onRestart, onBusyChange, reconnectConnectionId, initialLookoutWarehouse },
    ref,
  ) {
    const orpc = useOrpc();
    const queryClient = useQueryClient();
    const syntaxStyle = useSyntaxStyle();
    const [failedStep, setFailedStep] = React.useState<FailedStep | null>(null);
    const [expired, setExpired] = React.useState(false);
    const [formError, setFormError] = React.useState<string | null>(null);
    const lookoutForm = useForm<LookoutWarehouseValues>({
      resolver: zodResolver(LookoutWarehouseSchema),
      mode: 'onTouched',
      defaultValues: { lookoutWarehouse: initialLookoutWarehouse ?? '' },
    });
    const watchedLookoutWarehouse = useWatch({ control: lookoutForm.control, name: 'lookoutWarehouse' });
    const role = setup.options?.['role'];
    const grant = lookoutWarehouseGrantSQL(watchedLookoutWarehouse, typeof role === 'string' ? role : null);

    const verify = useMutation(
      orpc.settings.warehouses.verifyAccountSetup.mutationOptions({
        onSuccess: (result) => {
          void queryClient.invalidateQueries({ queryKey: orpc.settings.warehouses.key() });
          toast.success(
            result.adopted ? 'The account this setup created in RudderStack was linked.' : 'Account created',
          );
          // ACT2-1031: the account was saved, but the Lookout warehouse failed its check (the text says
          // whether it was dropped, or kept because the check could not run).
          if (result.lookoutWarehouseError) toast.warning(result.lookoutWarehouseError);
          onVerified();
        },
        onError: (err) => {
          refreshPermissionOnRefusal(queryClient, orpc, err);
          const e = err as RpcErrorShape;
          if (e.data?.reason === 'setup_expired' || e.data?.reason === 'setup_not_found') {
            setExpired(true);
            return;
          }
          // The record is kept server-side: fix the SQL, click Verify again.
          if (e.data?.failedStep) {
            setFailedStep(e.data.failedStep);
            return;
          }
          if (e.data?.reason === 'external_id_conflict_mismatch') {
            setFormError(
              'An account for these coordinates already exists with different settings. Remove it in RudderStack, then start again.',
            );
            return;
          }
          // Through the shared map, not `orpcErrorMessage`: a refusal thrown as
          // `new PlatformCreateRefusedError(reason)` has the code for its message, and verifying a
          // reconnect setup against the wrong host is exactly how QA saw `reconnect_target_invalid`
          // printed raw.
          setFormError(refusalMessage(err, 'Verification failed'));
        },
      }),
    );

    React.useEffect(() => {
      onBusyChange(verify.isPending);
    }, [verify.isPending, onBusyChange]);

    React.useImperativeHandle(
      ref,
      () => ({
        verify: () => {
          setFailedStep(null);
          setFormError(null);
          void lookoutForm.handleSubmit(({ lookoutWarehouse }) => {
            verify.mutate({
              type: 'SNOWFLAKE',
              setupId: setup.id,
              ...(reconnectConnectionId ? { reconnectConnectionId } : {}),
              // Always sent: the field starts from the stored value on a reconnect, so blank means cleared.
              lookoutWarehouse: lookoutWarehouse.trim(),
            });
          })();
        },
      }),
      [verify, setup.id, reconnectConnectionId, lookoutForm],
    );

    if (expired) {
      return (
        <Callout variant="error">
          <p>This setup expired or no longer exists.</p>
          <div className="mt-2">
            <Button type="button" variant="secondary" onClick={onRestart} label="Start again" />
          </div>
        </Callout>
      );
    }

    return (
      <div className="grid gap-5">
        {failedStep && <FailedStepCallout step={failedStep} />}
        {formError && <Callout variant="error">{formError}</Callout>}
        <Callout variant="highlight">
          Run this SQL in Snowflake as ACCOUNTADMIN, then click Verify. It stays valid until{' '}
          {new Date(setup.expiresAt).toLocaleString()}.
        </Callout>
        <OutputSection
          label="Setup SQL"
          action={
            <ButtonGroup>
              <DownloadButton text={setup.setupSql} filename="rudderstack-snowflake-setup.sql" />
              <CopyButton text={setup.setupSql} label="Copy" copyLabel="Setup SQL" />
            </ButtonGroup>
          }
        >
          <SyntaxHighlighter language="sql" style={syntaxStyle} customStyle={codeBlockCustomStyle}>
            {setup.setupSql}
          </SyntaxHighlighter>
        </OutputSection>
        <Form {...lookoutForm}>
          <form onSubmit={(e) => e.preventDefault()} className="grid gap-3" noValidate>
            <FormField
              control={lookoutForm.control}
              name="lookoutWarehouse"
              render={({ field }) => (
                <FormSection label="Lookout compute warehouse" optional description={LOOKOUT_WAREHOUSE_HINT}>
                  <Input {...field} placeholder="LOOKOUT_WH" disabled={verify.isPending} />
                </FormSection>
              )}
            />
          </form>
        </Form>
        {grant && (
          <OutputSection
            label="Also run"
            description="Lets the service role use the Lookout compute warehouse."
            action={<CopyButton text={grant} label="Copy" copyLabel="Grant" />}
          >
            <SyntaxHighlighter language="sql" style={syntaxStyle} customStyle={codeBlockCustomStyle}>
              {grant}
            </SyntaxHighlighter>
          </OutputSection>
        )}
        <OutputSection
          label="Public Key"
          action={<CopyButton text={setup.publicKey.pem} label="Copy" copyLabel="Public Key" />}
        >
          <pre className="font-mono text-xs whitespace-pre text-grey-800">{setup.publicKey.pem}</pre>
        </OutputSection>
        <OutputSection label="Fingerprint">
          <code className="block font-mono text-xs break-all">{setup.publicKey.fingerprint}</code>
        </OutputSection>
      </div>
    );
  },
);
